const CASH_BANK_CATEGORIES = ["cash_account", "bank_account"];

export function isCashBankLeaf(acc) {
  return CASH_BANK_CATEGORIES.includes(acc && acc.cashFlowCategory);
}

function sortByCode(a, b) {
  const codeA = String(a.code || "");
  const codeB = String(b.code || "");
  if (codeA && codeB && codeA !== codeB) return codeA.localeCompare(codeB);
  return String(a.title || "").localeCompare(String(b.title || ""));
}

export function toNumber(val) {
  const n = Number(val);
  return Number.isNaN(n) ? 0 : n;
}

export function parseIsoDateLocal(iso) {
  if (!iso) return null;
  const parts = String(iso).split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatIsoDate(d) {
  if (!d) return "";
  const dt =
    d instanceof Date
      ? d
      : typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)
      ? parseIsoDateLocal(d)
      : new Date(d);
  if (!dt || Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function getCurrentMonthRange() {
  const now = new Date();
  const dtFrom = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    dtFrom: formatIsoDate(dtFrom),
    dtTo: formatIsoDate(now),
  };
}

function groupLeavesByParent(leaves, byId) {
  const groups = {};

  leaves.forEach((leaf) => {
    const pid = Number(leaf.parentHead);
    const parent = byId[pid];
    const key = parent ? String(parent.id) : "other";

    if (!groups[key]) {
      groups[key] = {
        id: parent ? parent.id : 0,
        title: parent ? parent.title : "Accounts",
        code: parent ? parent.code : "",
        accounts: [],
      };
    }

    groups[key].accounts.push({
      ...leaf,
      displayBalance: toNumber(leaf.balance),
    });
  });

  return Object.values(groups)
    .map((group) => ({
      ...group,
      accounts: group.accounts.sort(sortByCode),
      totalBalance: group.accounts.reduce(
        (sum, acc) => sum + toNumber(acc.displayBalance),
        0
      ),
    }))
    .sort((a, b) => sortByCode(a, b));
}

export function buildCashBankSections(accounts) {
  const list = Array.isArray(accounts) ? accounts : [];
  const byId = {};
  list.forEach((acc) => {
    byId[acc.id] = acc;
  });

  const cashLeaves = list
    .filter((acc) => acc.cashFlowCategory === "cash_account")
    .sort(sortByCode);
  const bankLeaves = list
    .filter((acc) => acc.cashFlowCategory === "bank_account")
    .sort(sortByCode);

  const cashTotal = cashLeaves.reduce((sum, acc) => sum + toNumber(acc.balance), 0);
  const bankTotal = bankLeaves.reduce((sum, acc) => sum + toNumber(acc.balance), 0);

  return {
    cashGroups: groupLeavesByParent(cashLeaves, byId),
    bankGroups: groupLeavesByParent(bankLeaves, byId),
    cashAccounts: cashLeaves.map((acc) => ({
      ...acc,
      displayBalance: toNumber(acc.balance),
    })),
    bankAccounts: bankLeaves.map((acc) => ({
      ...acc,
      displayBalance: toNumber(acc.balance),
    })),
    cashTotal,
    bankTotal,
    netTotal: cashTotal + bankTotal,
    hasCash: cashLeaves.length > 0,
    hasBank: bankLeaves.length > 0,
  };
}

export function totalCashBankBalance(accounts) {
  return (Array.isArray(accounts) ? accounts : [])
    .filter(isCashBankLeaf)
    .reduce((sum, acc) => sum + toNumber(acc.balance), 0);
}
